// @flow
import { call, select } from 'redux-saga/effects';
import { CommonActions, StackActions } from '@react-navigation/native';
import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import { hasLoginInfoSelector } from '../Login';
import type { SignUpActionType } from '../actions';
import {
  hasBillingAddressSelector,
  hasCustomerIdentificationSelector,
  hasCustomPaymentMeanSelector,
} from '../../Customer/selectors';
import { regionSelector } from '../Region/selectors';
import { currentSubscriptionSelector } from '../../Dashboard';
import { shouldChooseAPlanForActivationSelector } from '../../Subscriptions';
import Navigation from '../../../lib/navigation';
import { initStateWithOffer } from '../../NewOffer/actions';
import { initStateWithOfferSaga } from '../../NewOffer/sagas';
import { newOfferPriceSelector } from '../../NewOffer/selectors';
import { isLoggedInSelector } from '../../Authentication';

export function* goToNextActivationPage(
  replaceRoute: boolean = false,
  hasNotYetSelectedSubscriptionToActivate: boolean = false
): Generator<*, *, *> {
  const navigateTo = routeName => {
    if (replaceRoute) {
      return StackActions.replace(routeName);
    }

    return CommonActions.navigate({ name: routeName });
  };

  const isLogged = yield select(isLoggedInSelector);
  const isCreatingAnAccount = yield select(hasLoginInfoSelector);

  if (!isLogged && !isCreatingAnAccount) {
    return yield call(Navigation.dispatch, navigateTo('authDuringActivate'));
  }

  const currentSubscription = yield select(currentSubscriptionSelector);
  const shouldChooseAPlanForActivation = yield select(shouldChooseAPlanForActivationSelector);

  if (!currentSubscription && shouldChooseAPlanForActivation && hasNotYetSelectedSubscriptionToActivate) {
    return yield call(Navigation.dispatch, navigateTo('selectSubscriptionToActivatePage'));
  }

  const offerPrice = yield select(newOfferPriceSelector);
  if (offerPrice === 0) {
    const region = yield select(regionSelector);
    if (!!TrpcLibrary.getRegions() && !region) {
      return yield call(Navigation.dispatch, navigateTo('chooseRegionDuringActivation'));
    }
    yield call(initStateWithOfferSaga, initStateWithOffer(null));
    return yield call(Navigation.dispatch, navigateTo('choosePlanDuringActivate'));
  }

  const hasCustomerIdentification = yield select(hasCustomerIdentificationSelector);
  const hasBillingAddress = yield select(hasBillingAddressSelector);
  const hasPaymentMean = yield select(hasCustomPaymentMeanSelector);
  if (hasCustomerIdentification && hasBillingAddress && hasPaymentMean) {
    return yield call(Navigation.dispatch, navigateTo('confirmPlanDuringActivate'));
  }

  if (!hasCustomerIdentification) {
    return yield call(Navigation.dispatch, navigateTo('identityRequirementsDuringActivate'));
  }

  if (!hasBillingAddress) {
    return yield call(Navigation.dispatch, navigateTo('billingAddressDuringActivate'));
  }
  if (!hasPaymentMean) {
    return yield call(Navigation.dispatch, navigateTo('registerPaymentDetailsDuringActivate'), {
      params: {
        redirectTo: 'confirmPlanDuringActivate',
      },
    });
  }
  return yield call(Navigation.dispatch, navigateTo('confirmPlanDuringActivate'));
}

export function* goToNextActivationPageSaga(action: SignUpActionType): Generator<*, *, *> {
  yield* goToNextActivationPage();
}
